import * as Sentry from '@sentry/react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

interface StripeProviderProps {
  children: ReactNode;
}

/**
 * `loadStripe` rejects when js.stripe.com cannot be fetched — most often an ad
 * blocker or privacy extension, which is what TENANT-ADMIN-PROD-30 ("Failed to
 * load Stripe.js", 36 users) turned out to be.
 *
 * Left unhandled, the rejected promise both reported an unactionable error and,
 * worse, left the payment UI mounted but inert: InvoicePayCheckoutModal's
 * handler is `if (!stripe) return`, so clicking Pay did nothing at all and said
 * nothing. Resolve to null instead and let callers render an explanation.
 */
const stripePromise = loadStripe(process.env.STRIPE_PUBLIC_KEY!).catch(
  (error: unknown) => {
    // Swallowing this entirely would make a misconfigured STRIPE_PUBLIC_KEY
    // or a Stripe outage (every customer affected) indistinguishable from one
    // person's ad blocker — with zero telemetry while the UI tells everyone
    // to disable their ad blocker. Warning level so the trickle of blocker
    // cases stays out of the error views; a fleet-wide failure shows up as a
    // volume spike.
    Sentry.captureMessage('Stripe.js failed to load', {
      level: 'warning',
      extra: { message: error instanceof Error ? error.message : String(error) },
    });
    return null;
  },
);

const StripeAvailabilityContext = createContext<boolean>(true);

/**
 * False once we know Stripe.js could not be loaded. Starts true so nothing
 * flashes an error during the normal load.
 */
export const useStripeAvailable = () => useContext(StripeAvailabilityContext);

/**
 * You can access stripe through the "useStripe" hook.
 * @example import { useStripe } from '@stripe/react-stripe-js';
 */
export const StripeProvider = ({ children }: StripeProviderProps) => {
  const [available, setAvailable] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void stripePromise.then((stripe) => {
      if (!cancelled && !stripe) setAvailable(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <StripeAvailabilityContext.Provider value={available}>
      <Elements options={{ locale: 'en' }} stripe={stripePromise}>
        {children}
      </Elements>
    </StripeAvailabilityContext.Provider>
  );
};
